// PANTHEON plate geometry, in source pixels of the 941 x 1672 plate
// (assets/v10/scenes/pantheon_base.png + pantheon_walls.png). The room holes mirror HOLES in scripts/build_pantheon_plate.py —
// change both together. Everything on screen is placed as plate px × scale.
import type { GearId } from '../olmps/ascension';

// The rotunda turns as one body: 120 deg per view, three views = one full turn.
// Every constant below mirrors scripts/build_pantheon_plate.py.
export const TURN_DEG = 120;

/** Walls, unwrapped onto a cylinder: texture column u <-> angle u / R_CYL; at
 *  rest plate x = CYL_CX + R_CYL sin(phi). Rows 0..WALL_BOTTOM of the plate. */
export const PANTHEON_WALLS = require('../../assets/v10/scenes/pantheon_walls.png');
export const CYL_CX = 471;
export const R_CYL = 470.5 / Math.sin(Math.PI / 3);
export const WALL_TEX_W = 1138;
export const WALL_BOTTOM = 1300;

/** Dome disc (top-down): perspective + rotateX(DOME_TILT) about its centre, spun in its own plane. */
export const PANTHEON_DOME = require('../../assets/v10/scenes/pantheon_dome.png');
export const DOME = { cx: 472, cy: 90.29, tiltDeg: (-0.91309 * 180) / Math.PI, p: 3078.9, r: 500 };

/** Floor medallion disc (top-down): orthographic rotateX, fixed in place, turning. */
export const PANTHEON_MEDALLION = require('../../assets/v10/scenes/pantheon_medallion.png');
export const MEDALLION = { cx: 472, cy: 1381, tiltDeg: (Math.acos(0.301) * 180) / Math.PI, r: 240 };

/** Static floor + front steps (rows FLOOR_TOP..end), medallion cut out. */
export const PANTHEON_FLOOR = require('../../assets/v10/scenes/pantheon_floor.png');
export const FLOOR_TOP = 1230;

/** Vertical perspective of the wall ring. A wall point at angle δ from the back
 *  sits (y − HORIZON_Y)·g(δ) from the horizon, g(δ) = (VIEW_D + 1)/(VIEW_D + cos δ)
 *  (viewer VIEW_D radii from the centre) — the sides rise/sink further than the
 *  back. Fitted to the mockup's cornice: y 345 at the back, 180 at ±60°. */
export const HORIZON_Y = 1000;
export const VIEW_D = 1.484;

/** Plate x -> wall texture column (the cylinder unwrap). */
export const wallU = (x: number) => R_CYL * Math.asin(Math.max(-1, Math.min(1, (x - CYL_CX) / R_CYL))) + WALL_TEX_W / 2;
export const PLATE_W = 941;
export const PLATE_H = 1672;
/** Plate px each side may be cropped on narrow phones (outer rooms start at x≈22). */
export const PLATE_CROP = 20;

/** One statue: podium centre-x, the podium's top (= where the feet stand),
 *  the figure's height and the podium's width — plate px. */
export interface Stand {
  cx: number;
  feet: number;
  h: number;
  pw: number;
}

export interface Room {
  gear: GearId;
  /** Transparent hole: left/right edge, arch apex, bottom (ledge / pedestal top). */
  x0: number;
  x1: number;
  apex: number;
  bottom: number;
  /** Centre stands in front; the two sides stand a step back. */
  main: Stand;
  sides: [Stand, Stand];
  /** Centre y of the Step's name above them (plain white caps for now). */
  tabletY: number;
  /** The gear name carved into the wall band over the arch: centre y (plate px) and tilt following the
   *  band's perspective slope (Joel, 2026-10-11: "almost like it's written in that space for real"). */
  label: { y: number; deg: number };
}

// pw = podium width; the lower rooms use the mockup pedestal at its own size.
// Figures ~18% taller (Joel, 2026-10-10); placards moved up into the arch heads to make room.
const UPPER = { main: { feet: 744, h: 334, pw: 160 }, side: { feet: 732, h: 278, pw: 132 } };
const LOWER = { main: { feet: 1263, h: 374, pw: 195 }, side: { feet: 1250, h: 311, pw: 160 } };

export const ROOMS: Room[] = [
  {
    gear: 'revise', x0: 58, x1: 250, apex: 368, bottom: 772, tabletY: 391, label: { y: 347, deg: 12 },
    main: { cx: 154, ...UPPER.main }, sides: [{ cx: 82, ...UPPER.side }, { cx: 226, ...UPPER.side }],
  },
  {
    gear: 'orient', x0: 372, x1: 570, apex: 424, bottom: 792, tabletY: 448, label: { y: 385, deg: 0 },
    main: { cx: 471, feet: 760, h: 300, pw: 150 }, sides: [{ cx: 400, feet: 748, h: 250, pw: 124 }, { cx: 542, feet: 748, h: 250, pw: 124 }],
  },
  {
    gear: 'regulate', x0: 690, x1: 870, apex: 368, bottom: 772, tabletY: 391, label: { y: 347, deg: -12 },
    main: { cx: 780, ...UPPER.main }, sides: [{ cx: 712, ...UPPER.side }, { cx: 848, ...UPPER.side }],
  },
  {
    gear: 'execute', x0: 25, x1: 260, apex: 826, bottom: 1246, tabletY: 847, label: { y: 808, deg: 6 },
    main: { cx: 142, ...LOWER.main }, sides: [{ cx: 62, ...LOWER.side }, { cx: 222, ...LOWER.side }],
  },
  {
    gear: 'direct', x0: 680, x1: 915, apex: 826, bottom: 1246, tabletY: 847, label: { y: 808, deg: -6 },
    main: { cx: 797, ...LOWER.main }, sides: [{ cx: 718, ...LOWER.side }, { cx: 876, ...LOWER.side }],
  },
];

/** Room clip windows run this far below the hole so podium bases stand on the ledge / floor. */
export const ROOM_FOOT = 64;

/** The mockup's own pedestal (scripts/build_pantheon_plate.py, 2x sprite of a
 *  195 x 58 plate-px crop): where the feet stand and where the name is engraved. */
export const PANTHEON_PODIUM = require('../../assets/v10/scenes/pantheon_podium.png');
export const PODIUM = { w: 195, h: 58, feetY: 15, bandY: 34.5, bandW: 150 };
/** Placard widths baked into the walls at each arch head (centre = Room.tabletY). */
export const PLACARD_W: Record<GearId, number> = { revise: 120, orient: 112, regulate: 120, execute: 124, direct: 124 };
export const PLACARD_H = 28;

/** Title + toggle band on the dome ring (the plate has them erased). */
export const TITLE_Y = 245;
export const TOGGLE_Y = 301;
export const TOGGLE_W = 455;
/** Footer line on the floor, between the two pedestals. */
export const FOOT_Y = 1334;
